/**
 * Bundled lesson catalog. Used in local-only mode so the app needs no
 * backend at all — works on any static host (Cloudflare Pages, Workers,
 * GitHub Pages, etc.).
 *
 * Mirrors db/migrations/0001_seed_global_lessons.sql + 0002_seed_image_urls.sql
 * + 0003_lesson_challenge_kinds.sql + 0004_fix_lookalike_family_images.sql.
 * If you change one, change the other.
 */
import type { ChallengeKind, ThemeId, TimeOfDay, WordDTO } from "@shared/types";
import { openmojiUrl } from "@shared/seed-images";

export interface SeedLesson {
  id: string;
  title: string;
  theme: ThemeId;
  timeOfDay: TimeOfDay;
  questionsCount: number;
  objectsCount: number;
  coinsCount: number;
  isGlobal: boolean;
  orderIndex: number;
  challengeKinds?: ChallengeKind[] | null;
  words: WordDTO[];
}

function ttsUrl(text: string): string | null {
  if (!text) return null;
  const slug = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `/audio/tts/${slug}.mp3`;
}

interface RawWord {
  id: string;
  english: string;
  vietnamese: string;
  sentence: string;
  /** OpenMoji codepoint, or null if no image. */
  code: string | null;
}

function w(id: string, english: string, vietnamese: string, sentence: string, code: string | null): RawWord {
  return { id, english, vietnamese, sentence, code };
}

function challengeKindsForLevel(orderIndex: number): ChallengeKind[] {
  if (orderIndex <= 5) return ["audio-to-image", "image-to-audio"];
  if (orderIndex < 10) return ["audio-to-english", "image-to-word", "image-to-audio"];
  return ["audio-to-english", "image-to-audio", "word-to-image", "image-to-word", "audio-to-image"];
}

const SEED: Array<Omit<SeedLesson, "words"> & { words: RawWord[] }> = [
  {
    id: "l_seed_01",
    title: "Level 1: People",
    theme: "forest",
    timeOfDay: "auto",
    questionsCount: 5,
    objectsCount: 14,
    coinsCount: 40,
    isGlobal: true,
    orderIndex: 1,
    challengeKinds: challengeKindsForLevel(1),
    words: [
      w("w_mother", "mother", "mẹ", "This is my mother.", "1F931"),
      w("w_father", "father", "bố", "This is my father.", "1F9D4"),
      w("w_brother", "brother", "anh/em trai", "This is my brother.", "1F466"),
      w("w_sister", "sister", "chị/em gái", "This is my sister.", "1F467"),
      w("w_teacher", "teacher", "giáo viên", "This is my teacher.", "1F9D1-200D-1F3EB"),
    ],
  },
  {
    id: "l_seed_02",
    title: "Level 2: Classroom",
    theme: "forest",
    timeOfDay: "morning",
    questionsCount: 5,
    objectsCount: 14,
    coinsCount: 40,
    isGlobal: true,
    orderIndex: 2,
    challengeKinds: challengeKindsForLevel(2),
    words: [
      w("w_friend", "friend", "bạn", "You are my friend.", "1F91D"),
      w("w_book", "book", "sách", "This is my book.", "1F4D8"),
      w("w_pen", "pen", "bút", "I have a pen.", "1F58A"),
      w("w_pencil", "pencil", "bút chì", "I write with a pencil.", "270F"),
      w("w_bag", "bag", "cặp", "My bag is blue.", "1F392"),
    ],
  },
  {
    id: "l_seed_03",
    title: "Level 3: Places",
    theme: "jungle",
    timeOfDay: "morning",
    questionsCount: 5,
    objectsCount: 14,
    coinsCount: 40,
    isGlobal: true,
    orderIndex: 3,
    challengeKinds: challengeKindsForLevel(3),
    words: [
      w("w_table", "table", "cái bàn", "The book is on the table.", "TABLE"),
      w("w_chair", "chair", "cái ghế", "I sit on a chair.", "1FA91"),
      w("w_door", "door", "cửa", "Open the door.", "1F6AA"),
      w("w_school", "school", "trường học", "I go to school.", "1F3EB"),
      w("w_rice", "rice", "cơm", "I eat rice.", "1F35A"),
    ],
  },
  {
    id: "l_seed_04",
    title: "Level 4: Food and Drink",
    theme: "candy",
    timeOfDay: "afternoon",
    questionsCount: 5,
    objectsCount: 14,
    coinsCount: 40,
    isGlobal: true,
    orderIndex: 4,
    challengeKinds: challengeKindsForLevel(4),
    words: [
      w("w_bread", "bread", "bánh mì", "I eat bread.", "1F35E"),
      w("w_apple", "apple", "táo", "I have an apple.", "1F34E"),
      w("w_banana", "banana", "chuối", "The banana is yellow.", "1F34C"),
      w("w_milk", "milk", "sữa", "I drink milk.", "1F95B"),
      w("w_juice", "juice", "nước ép", "I drink juice.", "1F9C3"),
    ],
  },
  {
    id: "l_seed_05",
    title: "Level 5: Animals",
    theme: "jungle",
    timeOfDay: "afternoon",
    questionsCount: 5,
    objectsCount: 16,
    coinsCount: 45,
    isGlobal: true,
    orderIndex: 5,
    challengeKinds: challengeKindsForLevel(5),
    words: [
      w("w_water", "water", "nước", "I drink water.", "1F4A7"),
      w("w_dog", "dog", "con chó", "The dog can run.", "1F436"),
      w("w_cat", "cat", "con mèo", "The cat is happy.", "1F431"),
      w("w_bird", "bird", "con chim", "The bird can fly.", "1F426"),
      w("w_fish", "fish", "con cá", "The fish is in water.", "1F41F"),
    ],
  },
  {
    id: "l_seed_06",
    title: "Level 6: Feelings",
    theme: "desert",
    timeOfDay: "sunset",
    questionsCount: 5,
    objectsCount: 16,
    coinsCount: 45,
    isGlobal: true,
    orderIndex: 6,
    challengeKinds: challengeKindsForLevel(6),
    words: [
      w("w_happy", "happy", "vui", "I am happy.", "1F642"),
      w("w_sad", "sad", "buồn", "I am sad.", "1F641"),
      w("w_angry", "angry", "tức giận", "I am angry.", "1F620"),
      w("w_tired", "tired", "mệt", "I am tired.", "1F634"),
      w("w_one", "one", "một", "I have one book.", "0031-FE0F-20E3"),
    ],
  },
  {
    id: "l_seed_07",
    title: "Level 7: Numbers",
    theme: "space",
    timeOfDay: "night",
    questionsCount: 5,
    objectsCount: 18,
    coinsCount: 50,
    isGlobal: true,
    orderIndex: 7,
    challengeKinds: challengeKindsForLevel(7),
    words: [
      w("w_two", "two", "hai", "I have two pens.", "0032-FE0F-20E3"),
      w("w_three", "three", "ba", "I see three cats.", "0033-FE0F-20E3"),
      w("w_four", "four", "bốn", "I see four birds.", "0034-FE0F-20E3"),
      w("w_five", "five", "năm", "I have five apples.", "0035-FE0F-20E3"),
      w("w_run", "run", "chạy", "I run.", "1F3C3"),
    ],
  },
  {
    id: "l_seed_08",
    title: "Level 8: Action Words 1",
    theme: "forest",
    timeOfDay: "morning",
    questionsCount: 5,
    objectsCount: 18,
    coinsCount: 50,
    isGlobal: true,
    orderIndex: 8,
    challengeKinds: challengeKindsForLevel(8),
    words: [
      w("w_walk", "walk", "đi bộ", "I walk to school.", "1F6B6"),
      w("w_jump", "jump", "nhảy", "I jump.", "1F998"),
      w("w_sleep", "sleep", "ngủ", "I sleep.", "1F6CF"),
      w("w_drink", "drink", "uống", "I drink milk.", "1F964"),
      w("w_eat", "eat", "ăn", "I eat bread.", "1F37D"),
    ],
  },
  {
    id: "l_seed_09",
    title: "Level 9: Action Words 2",
    theme: "snow",
    timeOfDay: "afternoon",
    questionsCount: 5,
    objectsCount: 18,
    coinsCount: 50,
    isGlobal: true,
    orderIndex: 9,
    challengeKinds: challengeKindsForLevel(9),
    words: [
      w("w_read", "read", "đọc", "I read a book.", "1F4D6"),
      w("w_write", "write", "viết", "I write with a pen.", "270D"),
      w("w_look", "look", "nhìn", "Look at the cat.", "1F440"),
      w("w_in", "in", "trong", "The fish is in water.", "1F4E5"),
      w("w_on", "on", "trên", "The book is on the table.", "2B06"),
    ],
  },
  {
    id: "l_seed_10",
    title: "Level 10: Builders",
    theme: "space",
    timeOfDay: "sunset",
    questionsCount: 5,
    objectsCount: 20,
    coinsCount: 55,
    isGlobal: true,
    orderIndex: 10,
    challengeKinds: challengeKindsForLevel(10),
    words: [
      w("w_under", "under", "dưới", "The cat is under the table.", "2B07"),
      w("w_i", "I", "tôi", "I am happy.", "1F464"),
      w("w_you", "you", "bạn", "You are my friend.", "1F449"),
      w("w_my", "my", "của tôi", "This is my book.", "1F64B"),
      w("w_your", "your", "của bạn", "This is your pen.", "1FAF5"),
    ],
  },
];

export const SEED_LESSONS: SeedLesson[] = SEED.map((l) => ({
  ...l,
  words: l.words.map((rw, idx) => ({
    id: rw.id,
    english: rw.english,
    vietnamese: rw.vietnamese,
    sentence: rw.sentence,
    imageUrl: openmojiUrl(rw.code),
    audioUrl: ttsUrl(rw.english),
    sentenceAudioUrl: ttsUrl(rw.sentence),
    orderIndex: idx,
  })),
}));

export function getSeedLesson(id: string): SeedLesson | undefined {
  return SEED_LESSONS.find((l) => l.id === id);
}
